import React, { useState } from 'react';
import { 
  Box, 
  Tooltip, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
  Button,
  Alert,
  Card,
  CardContent,
  Collapse
} from '@mui/material';
import { 
  AccountTree as AccountTreeIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetBusinessTrustchainQuery } from '../../redux/thunks/users/businesstrustchainsget';

const BusinessTrustChain = ({ businessId, businessName, isBusinessOwner = false }) => {
  const [open, setOpen] = useState(false);
  const [expandedEndorsers, setExpandedEndorsers] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Fetch business trust chain data using the RTK Query hook
  const { 
    data: responseData, 
    isLoading, 
    isError 
  } = useGetBusinessTrustchainQuery(businessId);

  // Extract data from the response
  const {
    business,
    browserUser,
    directConnection,
    endorsementConnections = [],
    totalEndorsements,
    connectedEndorsements,
    strongestConnection,
    trustScore,
    analytics
  } = responseData || {};

  // Don't render for business owners or if there's an error
  if (isBusinessOwner || isError) return null;

  // Show loading spinner
  if (isLoading) {
    return (
      <Tooltip title="Loading trust connections...">
        <Box sx={{ display: 'inline-flex', ml: 1, alignItems: 'center' }}>
          <div className="spinner-grow spinner-grow-sm" role="status" style={{ width: '0.8rem', height: '0.8rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </Box>
      </Tooltip>
    );
  }

  // Don't show if no endorsements or connections
  if (totalEndorsements === 0 || connectedEndorsements === 0) return null;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Helper to get initials from displayName
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('');
  };

  // Get connection type color
  const getConnectionTypeColor = (connectionType) => {
    switch (connectionType) {
      case 'direct': return 'success';
      case 'indirect': return 'primary';
      case 'self': return 'secondary';
      case 'no_connection': return 'default';
      default: return 'default';
    }
  };

  // Get trust score color
  const getTrustScoreColor = (score) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'warning.main';
    return 'error.main';
  };

  return (
    <>
      <Tooltip title={`Trust connections through ${connectedEndorsements} reference (Trust Score: ${trustScore})`}>
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
          }} 
          sx={{ 
            ml: 1,
            color: 'primary.main',
            '&:hover': {
              background: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          <PeopleIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Business Trust Chain Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <Box>
              <Typography variant="h6">
                Trust Connections to {businessName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your trust network connections through business references
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Trust Score Overview */}
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: 2 
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  gap: 2,
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <Box sx={{ textAlign: isMobile ? 'left' : 'center' }}>
                    <Typography 
                      variant={isMobile ? "h5" : "h4"}
                      sx={{ 
                        color: getTrustScoreColor(trustScore), 
                        fontWeight: 'bold'
                      }}
                    >
                      {trustScore}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trust Score
                    </Typography>
                  </Box>
                  
                  {!isMobile && <Divider orientation="vertical" flexItem />}
                  
                  <Box sx={{ flex: isMobile ? 'none' : 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {connectedEndorsements} of {totalEndorsements} references connected
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {analytics?.connectionRate}% connection rate
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Avg. path length: {analytics?.averagePathLength} hops
                    </Typography>
                    {analytics?.averageTrustWeight && (
                      <Typography variant="body2" color="text.secondary">
                        Avg. trust quality: {(analytics.averageTrustWeight * 100).toFixed(1)}%
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Chip 
                  label={analytics?.connectionStrength} 
                  color="primary" 
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    alignSelf: isMobile ? 'flex-start' : 'center',
                    mt: isMobile ? 1 : 0
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Direct Connection Alert */}
          {directConnection && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You own this business - maximum trust connection!
            </Alert>
          )}

          {/* Strongest Connection Display */}
          {strongestConnection && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  <StarIcon fontSize="small" sx={{ mr: 1, color: 'gold' }} />
                  Strongest Connection ({strongestConnection.pathLength} hop{strongestConnection.pathLength !== 1 ? 's' : ''})
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Tooltip title={`Trust Score: ${strongestConnection.endorser.trustScore || 'N/A'}`}>
                    <Avatar
                      onClick={() => navigate(`/profile/${strongestConnection.endorser.id}/active`)}
                      sx={{ 
                        width: 48, 
                        height: 48,
                        cursor: 'pointer',
                        border: '3px solid',
                        borderColor: 'primary.main',
                        background: strongestConnection.endorser.trustScore ? 
                          `linear-gradient(45deg, rgba(25, 118, 210, ${strongestConnection.endorser.trustScore / 100}) 50%, rgba(224, 224, 224, 0.3) 50%)` :
                          'rgba(224, 224, 224, 0.3)'
                      }}
                    >
                      {getInitials(strongestConnection.endorser.displayName)}
                    </Avatar>
                  </Tooltip>
                  <Box>
                    <Typography 
                      variant="subtitle1"
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/profile/${strongestConnection.endorser.id}/active`)}
                    >
                      {strongestConnection.endorser.displayName}
                      {strongestConnection.endorser.trustScore && (
                        <Typography 
                          component="span" 
                          variant="caption" 
                          sx={{ ml: 1, color: 'text.secondary' }}
                        >
                          (Trust: {strongestConnection.endorser.trustScore})
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip 
                        size="small" 
                        label={strongestConnection.connectionType}
                        color={getConnectionTypeColor(strongestConnection.connectionType)}
                        variant="outlined"
                      />
                      {strongestConnection.endorser.trustScore && (
                        <Chip 
                          size="small" 
                          label={`${strongestConnection.endorser.trustScore}% trusted`}
                          color={strongestConnection.endorser.trustScore >= 70 ? 'success' : 
                                strongestConnection.endorser.trustScore >= 50 ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Trust Chain Visualization for Strongest Connection */}
                {strongestConnection.trustChain && strongestConnection.trustChain.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: 'center',
                      justifyContent: isMobile ? 'center' : 'space-between',
                      width: '100%',
                      py: 2,
                      px: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      overflowX: isMobile ? 'visible' : 'auto'
                    }}
                  >
                    {strongestConnection.trustChain.map((user, index) => {
                      const isFirst = index === 0;
                      const isLast = index === strongestConnection.trustChain.length - 1;
                      const opacity = isFirst || isLast ? 1 : 1 - (index * 0.1);
                      
                      return (
                        <React.Fragment key={user.id}>
                          {/* User Node */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              width: isMobile ? '100%' : 'auto',
                              mb: isMobile ? 2 : 0,
                              minWidth: isMobile ? 'auto' : 80
                            }}
                          >
                            <Tooltip title={`Trust Score: ${user.trustScore || 'N/A'}`}>
                              <Avatar
                                src={user.profileImageUrl}
                                alt={user.displayName}
                                onClick={() => navigate(`/profile/${user.id}/active`)}
                                sx={{ 
                                  width: isFirst || isLast ? 56 : 48, 
                                  height: isFirst || isLast ? 56 : 48,
                                  opacity,
                                  border: '3px solid',
                                  borderColor: user.isSource ? 'success.main' : user.isTarget ? 'primary.main' : 'grey.400',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.5)',
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {getInitials(user.displayName)}
                              </Avatar>
                            </Tooltip>
                            
                            <Typography 
                              variant={isFirst || isLast ? "subtitle2" : "caption"} 
                              align="center"
                              sx={{ 
                                mt: 1, 
                                fontWeight: isFirst || isLast ? 'bold' : 'normal',
                                maxWidth: 80,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: isMobile ? 'normal' : 'nowrap'
                              }}
                            >
                              {user.isSource ? 'You' : user.isTarget ? 'Reference' : user.displayName}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              align="center"
                              sx={{ mt: 0.5 }}
                            >
                              {user.isSource ? 'Start' : user.isTarget ? 'Target' : `Hop ${index}`}
                            </Typography>
                          </Box>
                          
                          {/* Connection Line */}
                          {!isLast && (
                            <>
                              {isMobile ? (
                                <Box 
                                  sx={{ 
                                    height: 30, 
                                    width: 2, 
                                    bgcolor: 'primary.main',
                                    opacity: 0.5,
                                    mb: 1
                                  }} 
                                />
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                                  <Box 
                                    sx={{ 
                                      height: 2, 
                                      width: 20, 
                                      bgcolor: 'primary.main',
                                      opacity: 0.5
                                    }} 
                                  />
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      opacity: 0.6,
                                      mx: 0.5
                                    }}
                                  />
                                  <Box 
                                    sx={{ 
                                      height: 2, 
                                      width: 20, 
                                      bgcolor: 'primary.main',
                                      opacity: 0.5
                                    }} 
                                  />
                                </Box>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* All Endorsers Section */}
          <Card>
            <CardContent>
              <Button
                onClick={() => setExpandedEndorsers(!expandedEndorsers)}
                endIcon={expandedEndorsers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 2 }}
                fullWidth={isMobile}
              >
                All References ({totalEndorsements})
              </Button>
              
              <Collapse in={expandedEndorsers}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: isMobile ? 1.5 : 2 
                }}>
                  {endorsementConnections.map((connection, index) => (
                    <Card 
                      key={index} 
                      variant="outlined" 
                      sx={{ 
                        bgcolor: connection.connected ? 'success.50' : 'grey.50',
                        '&:hover': {
                          boxShadow: 1
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        py: isMobile ? 1 : 1.5,
                        '&:last-child': { pb: isMobile ? 1 : 1.5 }
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          flexWrap: isMobile ? 'wrap' : 'nowrap',
                          gap: isMobile ? 1 : 2
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            flex: 1,
                            minWidth: 0
                          }}>
                            <Avatar
                              onClick={() => connection.endorser.id !== 'self' && navigate(`/profile/${connection.endorser.id}/active`)}
                              sx={{ 
                                width: isMobile ? 32 : 36, 
                                height: isMobile ? 32 : 36,
                                cursor: connection.endorser.id !== 'self' ? 'pointer' : 'default',
                                border: connection.connected ? '2px solid' : '1px solid',
                                borderColor: connection.connected ? 'success.main' : 'grey.300'
                              }}
                            >
                              {getInitials(connection.endorser.displayName)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant={isMobile ? "body2" : "body1"}
                                sx={{ 
                                  cursor: connection.endorser.id !== 'self' ? 'pointer' : 'default',
                                  '&:hover': connection.endorser.id !== 'self' ? { 
                                    color: 'primary.main', 
                                    textDecoration: 'underline' 
                                  } : {},
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                onClick={() => connection.endorser.id !== 'self' && navigate(`/profile/${connection.endorser.id}/active`)}
                              >
                                {connection.endorser.displayName}
                                {connection.endorser.trustScore && (
                                  <Typography 
                                    component="span" 
                                    variant="caption" 
                                    sx={{ ml: 1, color: 'text.secondary' }}
                                  >
                                    ({connection.endorser.trustScore})
                                  </Typography>
                                )}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {connection.connected 
                                  ? `${connection.pathLength} hop${connection.pathLength !== 1 ? 's' : ''} away`
                                  : 'Not connected'
                                }
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Chip 
                            size="small" 
                            label={connection.connectionType.replace('_', ' ')}
                            color={getConnectionTypeColor(connection.connectionType)}
                            variant={connection.connected ? "filled" : "outlined"}
                            sx={{
                              minWidth: isMobile ? 'auto' : 'fit-content',
                              fontSize: isMobile ? '0.7rem' : '0.75rem'
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="caption" color="text.secondary">
            Trust connections show how you're connected to people who have referenced this business. 
            Trust scores reflect both network distance and individual user credibility. Higher referencer trust scores 
            contribute more value to the overall business trust rating.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BusinessTrustChain;